/**
 * A fila local de envios, recortada pela conversa aberta (H06).
 *
 * ── Por que uma fila, e não um booleano `enviando` ────────────────────────
 *
 * A H04 tinha um booleano, e ele resolve o caso de um envio por vez. Não
 * resolve os dois que a H06 pede:
 *
 *   O envio que FALHOU precisa continuar na tela, com o texto intacto e a
 *   mesma `Idempotency-Key`, esperando o "tentar de novo". Com um booleano, o
 *   texto vive no campo, e escrever outra coisa o apaga.
 *
 *   E dois envios podem coexistir: o vendedor manda uma mensagem, o servidor
 *   demora, ele manda a segunda. Um booleano faria a segunda parecer a
 *   primeira.
 *
 * ── O campo esvazia no clique; o texto não se perde ──────────────────────
 *
 * Parece contradizer a §11.1, e não contradiz. O que ela proíbe é mostrar como
 * ENVIADA uma mensagem que a API ainda pode recusar — e aqui ela aparece como
 * `pendente`, esmaecida e sem carimbo de hora.
 *
 * O texto migra do campo para a fila, que é o único lugar onde ele sobrevive a
 * uma recusa. Na H04, que limpava só depois da aceitação, o vendedor ficava com
 * o campo travado esperando a resposta — e se resolvesse escrever outra coisa,
 * perdia o que estava lá.
 */

import { useCallback, useState } from 'react';
import type { ClienteDaApi } from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import { corpoComAnexos } from '../lib/anexos';
import { criarEnvio, repetirEnvio, type EnvioLocal } from '../lib/envio';

export interface Envio {
  readonly envios: readonly EnvioLocal[];
  readonly enviar: (
    conteudo: string,
    privada: boolean,
    templateNome?: string,
    anexos?: readonly File[],
    /** Um valor por variável do modelo, na ordem. */
    templateValores?: readonly string[],
    /** Quem a nota interna chama com "@" (#569). */
    mencoes?: readonly string[],
  ) => void;
  readonly repetir: (id: string) => void;
  readonly descartar: (id: string) => void;
  /** `true` enquanto houver envio esperando resposta. */
  readonly ocupado: boolean;
}

export function useEnvio(
  api: ClienteDaApi | null,
  conversaId: string | null,
  invalidar: (chaves: readonly (readonly unknown[])[]) => void,
): Envio {
  const [envios, definirEnvios] = useState<readonly EnvioLocal[]>([]);

  const atualizar = useCallback((id: string, mudanca: Partial<EnvioLocal>) => {
    definirEnvios((atuais) =>
      atuais.map((envio) => (envio.id === id ? { ...envio, ...mudanca } : envio)),
    );
  }, []);

  const despachar = useCallback(
    (envio: EnvioLocal) => {
      /*
       * A conversa vem do ENVIO, e não do estado da tela.
       *
       * Vinha do estado até 02/09/2026, e isso tornava o "tentar de novo" uma
       * armadilha: quem trocasse de conversa antes de repetir mandaria a
       * mensagem para outra pessoa. O envio carrega a sua conversa desde que
       * nasce, e é essa que vale em toda tentativa — pela mesma razão que a
       * `Idempotency-Key` não muda no retry.
       */
      if (!api) return;
      const destino = envio.conversaId;

      /*
       * Com arquivo, o corpo vira `FormData`; sem, continua sendo JSON.
       *
       * O cliente do contrato reconhece `FormData` e deixa o `fetch` escrever o
       * `content-type` com o boundary — tudo o mais (autorização, correlação,
       * idempotência, tratamento de erro) segue igual. Uma rota só, dois
       * formatos de corpo, porque é o mesmo comando.
       */
      const corpo =
        envio.anexos.length > 0
          ? corpoComAnexos(
              envio.conteudo,
              envio.privada,
              envio.templateNome,
              envio.anexos,
              envio.mencoes,
            )
          : {
              conteudo: envio.conteudo,
              privada: envio.privada,
              ...(envio.templateNome ? { templateNome: envio.templateNome } : {}),
              // Os valores acompanham o modelo: sem eles a Meta recusa por
              // contagem, com um erro que fala em número e não em qual faltou.
              // Cópia mutável: o contrato do zod pede `string[]`, e o nosso
              // envio guarda `readonly` para ninguém alterar a fila por
              // engano.
              ...((envio.templateValores?.length ?? 0) > 0
                ? { templateValores: [...(envio.templateValores ?? [])] }
                : {}),
              ...(envio.mencoes.length > 0 ? { mencoes: [...envio.mencoes] } : {}),
            };

      void api
        .enviarMensagem({
          caminho: { id: destino },
          corpo,
          // A MESMA chave em toda tentativa. Ver o cabeçalho de `envio.ts`.
          idempotencyKey: envio.chave,
        })
        .then((resposta) => {
          // Só AGORA o otimismo é permitido: a API aceitou.
          atualizar(envio.id, { estado: 'aceita', aceitoEm: resposta.criadaEm, erro: null });

          // O SSE também vai invalidar, mas depende do fluxo estar de pé. Quem
          // acabou de agir precisa ver o resultado mesmo com o tempo real
          // caído.
          invalidar([chaves.conversa(destino), chaves.inbox()]);
        })
        .catch((erro: unknown) => {
          atualizar(envio.id, { estado: 'falhou', erro });
        });
    },
    [api, atualizar, invalidar],
  );

  const enviar = useCallback(
    (
      conteudo: string,
      privada: boolean,
      templateNome?: string,
      anexos: readonly File[] = [],
      templateValores?: readonly string[],
      mencoes: readonly string[] = [],
    ) => {
      if (!conversaId) return;

      const envio = criarEnvio(
        conversaId,
        conteudo,
        privada,
        templateNome,
        new Date(),
        anexos,
        templateValores,
        mencoes,
      );
      definirEnvios((atuais) => [...atuais, envio]);
      despachar(envio);
    },
    [conversaId, despachar],
  );

  const repetir = useCallback(
    (id: string) => {
      definirEnvios((atuais) => {
        const alvo = atuais.find((envio) => envio.id === id);
        if (!alvo || alvo.estado === 'pendente') return atuais;

        const novo = repetirEnvio(alvo);
        despachar(novo);
        return atuais.map((envio) => (envio.id === id ? novo : envio));
      });
    },
    [despachar],
  );

  /**
   * Descartar é do usuário, e existe para o caso sem saída.
   *
   * Falha que não dá para repetir — janela fechada, sem permissão — deixaria o
   * envio na tela para sempre. Sem uma forma de tirá-lo, o histórico acumula
   * tentativas mortas e a conversa fica ilegível.
   */
  const descartar = useCallback((id: string) => {
    definirEnvios((atuais) => atuais.filter((envio) => envio.id !== id));
  }, []);

  /*
   * A fila guarda TUDO; o que sai daqui é só desta conversa.
   *
   * Guardar tudo é o que faz um envio que falhou sobreviver a uma troca de
   * conversa — ele continua lá, com o texto e os arquivos, esperando o
   * "tentar de novo" quando a pessoa voltar. Foi para isso que a H06 trocou o
   * booleano por uma fila.
   *
   * Filtrar na saída é o que impede o defeito de 02/09/2026, em que a mesma
   * mensagem aparecia em duas conversas. O `ocupado` vai junto: um envio
   * pendente noutra conversa não tem por que travar o campo desta.
   */
  const daConversa = envios.filter((envio) => envio.conversaId === conversaId);

  return {
    envios: daConversa,
    enviar,
    repetir,
    descartar,
    ocupado: daConversa.some((envio) => envio.estado === 'pendente'),
  };
}
